"use client";

import Image from "next/image";
import { useState } from "react";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaWhatsapp,
} from "react-icons/fa";
import { sendContactForm } from "@/lib/api";

const DIGITAL_OCEAN_SPACES_CDN =
  "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | "">(
    "",
  );

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "firstName":
        if (!value.trim()) return "First Name is required";
        if (/[0-9]/.test(value)) return "First Name cannot contain numbers";
        if (/[^a-zA-Z\s]/.test(value))
          return "First Name cannot contain special characters";
        if (value.trim().length > 100)
          return "First Name cannot exceed 100 characters";
        return "";

      case "lastName":
        if (!value.trim()) return "Last Name is required";
        if (/[0-9]/.test(value)) return "Last Name cannot contain numbers";
        if (/[^a-zA-Z\s]/.test(value))
          return "Last Name cannot contain special characters";
        if (value.trim().length > 100)
          return "Last Name cannot exceed 100 characters";
        return "";

      case "email":
        if (!value.trim()) return "Email is required";
        if (!value.includes("@")) return 'Email must contain "@" symbol';
        if (!emailRegex.test(value))
          return "Please enter a valid email address";
        if (value.trim().length > 320)
          return "Email cannot exceed 320 characters";
        return "";

      case "message":
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10)
          return "Message must be at least 10 characters";
        if (value.trim().length > 3000)
          return "Message cannot exceed 3000 characters";
        return "";

      default:
        return "";
    }
  };

  // Handle input change with validation
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate field and update errors
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    // Clear submit message when user starts typing
    if (submitMessage) {
      setSubmitMessage("");
      setSubmitStatus("");
    }
  };

  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors = {
      firstName: validateField("firstName", formData.firstName),
      lastName: validateField("lastName", formData.lastName),
      email: validateField("email", formData.email),
      message: validateField("message", formData.message),
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error !== "");
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setSubmitMessage("Please fix the errors above");
      setSubmitStatus("error");
      return;
    }

    setLoading(true);
    setSubmitMessage("");

    try {
      // Trim all values before sending
      const trimmedData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };

      const response = await sendContactForm(trimmedData);

      setLoading(false);

      if (!response.hasErrors && response.data?.uid) {
        setSubmitMessage(
          "✅ Thank you! Your message has been sent successfully. We'll get back to you soon.",
        );
        setSubmitStatus("success");

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
        });
        setErrors({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
        });
      } else {
        const errorMsg =
          response.errors
            ?.map((e) => e.description)
            .filter(Boolean)
            .join(", ") ||
          "Failed to send message. Please try again or contact us via email.";
        setSubmitMessage(`❌ ${errorMsg}`);
        setSubmitStatus("error");
      }
    } catch (error) {
      setLoading(false);
      setSubmitMessage(
        "❌ Network error. Please check your connection and try again.",
      );
      setSubmitStatus("error");
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      message: "",
    });
    setErrors({
      firstName: "",
      lastName: "",
      email: "",
      message: "",
    });
    setSubmitMessage("");
    setSubmitStatus("");
  };

  // Handle Enter key press in form
  const handleKeyPress = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Check if form has any data
  const hasFormData =
    formData.firstName ||
    formData.lastName ||
    formData.email ||
    formData.message;

  return (
    <div className="min-h-screen bg-[#faf8f5] dark:bg-[#0a0a0a] pt-16 md:pt-20">
      {/* FULL-WIDTH CREATIVE HEADER (same style as Support/FAQ) */}
      <header className="relative w-full overflow-hidden border-b border-gray-200 dark:border-gray-700">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage:
              "url('https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com/Site/images/background/support_background-2.jpg')",
            filter: "contrast(1.1) saturate(1.15)",
            transform: "scale(1.03)",
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 via-black/10 to-indigo-900/20" />

        <div className="pointer-events-none absolute -top-24 -left-28 h-80 w-80 rounded-full bg-purple-300/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -right-28 h-96 w-96 rounded-full bg-indigo-300/20 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="flex items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-4xl font-extrabold text-white tracking-tight drop-shadow-sm">
                Contact Us
              </h1>
              <div className="mt-4 h-1 w-24 rounded-full bg-white/90" />
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </header>

      {/* Hero Section (glassy like Support) */}
      <div className="relative max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          {/* Left: Text card */}
          <div className="lg:col-span-7">
            <div className="h-full rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="p-8 md:p-10">
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                  <span className="text-purple-600 text-3xl font-extrabold">
                    "
                  </span>
                  AyuBowan,
                  <br />
                  and Welcome to Lily Lanka
                  <span className="text-purple-600 text-3xl font-extrabold">
                    "
                  </span>
                </h2>

                <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
                  Your journey through Sri Lanka deserves thoughtful planning,
                  refined experiences, and a team that is always within reach.
                  At Lily Lanka, we believe meaningful travel begins with
                  personal connection. Whether you are dreaming of a serene
                  beach escape, a cultural immersion, or a carefully curated
                  adventure, our team is here to assist you every step of the
                  way.
                  <br />
                  <br />
                  From your very first inquiry to the moment your journey
                  concludes, we are committed to providing attentive service,
                  clear communication, and genuine local expertise.
                </p>
              </div>

              <div className="h-2 w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600" />
            </div>
          </div>

          {/* Right: Image card */}
          <div className="lg:col-span-5">
            <div className="h-full rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
              {/* Make this area stretch */}
              <div className="p-8 md:p-10 h-full flex items-stretch justify-center">
                {/* Control width here (adjust max-w as you like) */}
                <div className="relative w-full max-w-[320px] h-full">
                  {/* soft glow behind image */}
                  <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 blur-2xl" />

                  <div className="relative h-full w-full rounded-2xl border border-gray-200/60 dark:border-gray-700 bg-white/60 dark:bg-[#27272a]/60 p-4 shadow-sm">
                    <div className="relative h-[300px] lg:h-full w-full">
                      <Image
                        src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/contact-us.jpeg`}
                        alt="Sri Lankan Art"
                        fill
                        className="rounded-xl object-contain"
                        sizes="(min-width: 1024px) 320px, 100vw"
                        priority
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="h-2 w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600" />
            </div>
          </div>
        </div>

        {/* Need a hand faster (same content, nicer layout) */}
        <div className="mt-12 rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
          <div className="p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
              <div className="md:col-span-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Need a hand faster?
                </h3>
                <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  Send us a WhatsApp message — we reply faster than a tuk-tuk in
                  Galle traffic!
                </p>
              </div>

              <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ContactCard title="Email" value="lilylanka@outlook.com" />
                <ContactCard
                  title="Website"
                  value="www.lilylanka.com"
                  link="https://www.lilylanka.com"
                />
                <ContactCard
                  title="Hotline & WhatsApp"
                  value="+94 77 209 0344"
                />
                <ContactCard title="Our Nest" value="Colombo, Sri Lanka" />
              </div>
            </div>
          </div>

          <div className="h-2 w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600" />
        </div>

        {/* Send Us a Quick Message (glassy + premium inputs) */}
        <div className="mt-16 rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 ...">
          <div className="p-8 md:p-10">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Send Us a Quick Message
            </h3>
            <p className="text-base text-gray-600 dark:text-gray-400 mb-8">
              We invite you to reach out and share your plans, questions, or
              ideas. Every message is read with care, and every inquiry is
              treated as the beginning of something special.
            </p>

            <form
              onSubmit={handleSubmit}
              onKeyPress={handleKeyPress}
              className="rounded-2xl bg-white/60 dark:bg-[#27272a]/60 border border-gray-200/70 dark:border-gray-700 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* First Name */}
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm bg-white/80 dark:bg-[#27272a] dark:text-gray-200 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                      errors.firstName
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    aria-invalid={!!errors.firstName}
                    aria-describedby={
                      errors.firstName ? "firstName-error" : undefined
                    }
                  />
                  {errors.firstName && (
                    <p
                      id="firstName-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.firstName}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm bg-white/80 dark:bg-[#27272a] dark:text-gray-200 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                      errors.lastName
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    aria-invalid={!!errors.lastName}
                    aria-describedby={
                      errors.lastName ? "lastName-error" : undefined
                    }
                  />
                  {errors.lastName && (
                    <p
                      id="lastName-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.lastName}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="md:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm bg-white/80 dark:bg-[#27272a] dark:text-gray-200 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                      errors.email
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <p
                      id="email-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={3000}
                  className={`w-full border-2 rounded-xl px-4 py-3 text-sm bg-white/80 dark:bg-[#27272a] dark:text-gray-200 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                    errors.message
                      ? "border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                  aria-invalid={!!errors.message}
                  aria-describedby={
                    errors.message ? "message-error" : undefined
                  }
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.message ? (
                    <p
                      id="message-error"
                      className="text-red-500 text-xs"
                      role="alert"
                    >
                      {errors.message}
                    </p>
                  ) : (
                    <span />
                  )}
                  <p
                    className={`text-xs ml-auto ${
                      formData.message.length > 2900
                        ? "text-red-500"
                        : "text-gray-400"
                    }`}
                  >
                    {formData.message.length} / 3000
                  </p>
                </div>
              </div>

              {/* Submit Message */}
              {submitMessage && (
                <div
                  className={`mb-6 p-4 rounded-xl border ${
                    submitStatus === "success"
                      ? "bg-green-50 text-green-800 border-green-200"
                      : "bg-red-50 text-red-800 border-red-200"
                  }`}
                  role="alert"
                >
                  <p className="text-sm">{submitMessage}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={!hasFormData || loading}
                  className="px-8 py-2 rounded-xl text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white/70 dark:bg-[#27272a] hover:bg-white dark:hover:bg-[#3f3f46] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-2 rounded-xl text-sm font-medium border-2 border-purple-600 text-purple-600 bg-white/70 hover:bg-purple-700 hover:text-white transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>

          <div className="h-2 w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600" />
        </div>

        {/* Stay Connected (same content, glass frame) */}
        <div className="mt-20 pb-8 text-center">
          <div className="rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
            <div className="p-8 md:p-10">
              <h4 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Stay Connected
              </h4>

              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                Let Lily Lanka inspire your journey beyond the screen. Follow us
                to discover hidden gems, refined travel moments, and authentic
                island experiences curated for modern explorers.
                <br />
                <br />
                Join our community on
              </p>

              <div className="flex justify-center gap-4 text-gray-700">
                <SocialIcon href="https://www.facebook.com/share/15FprWU6HPt/?mibextid=wwXIfr">
                  <FaFacebookF />
                </SocialIcon>
                <SocialIcon href="https://www.instagram.com/lily__lanka?igsh=MTdjY2V2dTQ2MXFzdg%3D%3D&utm_source=q">
                  <FaInstagram />
                </SocialIcon>
                <SocialIcon href="https://www.tiktok.com/@lily.lanka?_t=ZS-901fAQ9f4fF&_r=1">
                  <FaTiktok />
                </SocialIcon>
                <SocialIcon href="https://www.youtube.com/@LilyLanka">
                  <FaYoutube />
                </SocialIcon>
                <SocialIcon href="https://wa.me/94772090344">
                  <FaWhatsapp />
                </SocialIcon>
              </div>
            </div>

            <div className="h-2 w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600" />
          </div>
        </div>

        <div className="h-10" />
      </div>
    </div>
  );
}

/* Reusable components */

function ContactCard({
  title,
  value,
  link,
}: {
  title: string;
  value: string;
  link?: string;
}) {
  return (
    <div className="bg-white dark:bg-[#18181b] border-2 border-purple-200 dark:border-purple-900 rounded-xl p-6 hover:border-purple-400 transition">
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
        {title}
      </p>
      {link ? (
        <a // ✅ Added
          href={link}
          className="text-purple-600 text-base font-semibold hover:underline"
        >
          {value}
        </a>
      ) : (
        <p className="text-gray-900 dark:text-white text-base font-semibold">
          {value}
        </p>
      )}
    </div>
  );
}

function SocialIcon({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a // ✅ Added
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-11 h-11 flex items-center justify-center border-2 border-gray-300 dark:border-gray-600 rounded-full hover:text-purple-600 hover:border-purple-600 transition text-lg"
    >
      {children}
    </a>
  );
}
